import {
  resolvedStudioMediaMimeType,
  studioMediaImportSpec,
  validateStudioMediaFile,
  type StudioImportableMediaType,
} from "./studio-media-import";

const MEDIA_TYPES: StudioImportableMediaType[] = [
  "image",
  "document",
  "video",
  "animation",
  "simulation",
];

function currentBlockType(editor: HTMLElement): StudioImportableMediaType | undefined {
  const id = editor.querySelector(".studio-editor-heading code")?.textContent?.trim();
  if (!id) return undefined;
  const card = [...document.querySelectorAll<HTMLElement>("[data-block-id]")].find(
    (node) => node.dataset.blockId === id,
  );
  const preview = card?.querySelector<HTMLElement>(".studio-student-block");
  if (!preview) return undefined;
  return MEDIA_TYPES.find((type) => preview.classList.contains(`type-${type}`));
}

function fieldInput(root: HTMLElement, labelPrefix: string) {
  return [...root.querySelectorAll<HTMLLabelElement>("label.studio-field")]
    .find((label) =>
      label.querySelector("span")?.textContent?.trim().startsWith(labelPrefix),
    )
    ?.querySelector<HTMLInputElement>("input");
}

function setReactInputValue(input: HTMLInputElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    "value",
  )?.set;
  setter?.call(input, value);
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
}

function fileAsDataUrl(file: File, mimeType: string) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Lecture du fichier impossible."));
    reader.onload = () => {
      const raw = String(reader.result || "");
      const encoded = raw.split(",", 2)[1];
      if (!encoded) return reject(new Error("Fichier illisible."));
      resolve(`data:${mimeType};base64,${encoded}`);
    };
    reader.readAsDataURL(file);
  });
}

const nextFrame = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

function enhanceStudioMediaImport() {
  if (location.pathname !== "/activity-studio") return;
  const editor = document.querySelector<HTMLElement>(".studio-block-editor");
  if (!editor) return;
  const type = currentBlockType(editor);
  if (!type) return;
  const resourceFieldset = [...editor.querySelectorAll<HTMLFieldSetElement>("fieldset")].find(
    (fieldset) => fieldset.querySelector("legend")?.textContent?.trim() === "Ressource",
  );
  if (!resourceFieldset) return;

  const existing = resourceFieldset.querySelector<HTMLElement>(
    ".studio-direct-media-import",
  );
  if (existing?.dataset.mediaType === type) return;
  existing?.remove();

  const spec = studioMediaImportSpec(type);
  if (!spec) return;

  const wrap = document.createElement("div");
  wrap.className = "studio-direct-media-import";
  wrap.dataset.mediaType = type;
  wrap.style.cssText =
    "display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:10px 0 12px;border-bottom:1px solid #dbe7ef;margin-bottom:12px";

  const button = document.createElement("button");
  button.type = "button";
  button.className = "studio-primary";
  button.textContent = `📁 ${spec.label}`;

  const input = document.createElement("input");
  input.type = "file";
  input.accept = spec.accept;
  input.hidden = true;

  const status = document.createElement("small");
  status.style.cssText = "color:#60758a;flex:1;min-width:180px";
  status.textContent = "Le fichier sera embarqué dans le JSON de l’activité.";

  button.addEventListener("click", () => input.click());
  input.addEventListener("change", async () => {
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    const error = validateStudioMediaFile(type, file);
    if (error) {
      status.textContent = `⚠ ${error}`;
      status.style.color = "#b42318";
      return;
    }
    try {
      button.disabled = true;
      status.style.color = "#60758a";
      status.textContent = "Import en cours…";
      const mimeType = resolvedStudioMediaMimeType(type, file);
      const dataUrl = await fileAsDataUrl(file, mimeType);

      let currentEditor = document.querySelector<HTMLElement>(".studio-block-editor");
      if (!currentEditor) throw new Error("Éditeur introuvable.");
      const nameInput = fieldInput(currentEditor, "Nom de la ressource");
      if (nameInput) {
        setReactInputValue(nameInput, file.name);
        await nextFrame();
      }

      currentEditor = document.querySelector<HTMLElement>(".studio-block-editor");
      if (!currentEditor) throw new Error("Éditeur introuvable.");
      const urlInput = fieldInput(currentEditor, "Adresse de la ressource");
      if (!urlInput) throw new Error("Champ de ressource introuvable.");
      setReactInputValue(urlInput, dataUrl);
      await nextFrame();

      status.textContent = `✓ ${file.name} importé · ${(file.size / 1000).toFixed(0)} ko · inclus dans le JSON`;
      status.style.color = "#087443";
    } catch (reason) {
      status.textContent = `⚠ ${reason instanceof Error ? reason.message : "Import impossible."}`;
      status.style.color = "#b42318";
    } finally {
      button.disabled = false;
    }
  });

  wrap.append(button, input, status);
  const legend = resourceFieldset.querySelector("legend");
  if (legend?.nextSibling) resourceFieldset.insertBefore(wrap, legend.nextSibling);
  else resourceFieldset.append(wrap);
}

if (typeof document !== "undefined") {
  const observer = new MutationObserver(() => enhanceStudioMediaImport());
  const start = () => {
    enhanceStudioMediaImport();
    observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
}
